import React from 'react';
import styled from '@emotion/styled';
import { useButton, useDeviceInfo, useI18n, useLogger, useSelector, useStaffCan } from '~Hooks';
import { BlueButton } from '~Styles';
import useModal from '~ComponentsImproved/Modal/useModal';
import Modal from '~ComponentsImproved/Modal';
import SplitPayment from './SplitPayment';
import PayWithButton from '~Components/PayWithButton';

const Wrapper = styled.div`
  position: relative;
  flex: 1;
`;

interface Props {
  selectedChequeId?: string;
  paidAmount?: number; // Used to exclude INVOICE as a payment method if there is a partial payment
  onSelect: (methodId: string, customAmount?: number, variant?: string) => void;
  isHandyQSR?: boolean;
}

/**
 *
 * @param paidAmount Used to exclude INVOICE as a payment method if there is a partial payment
 * @param onSelect Callback when a payment method is selected
 * @param selectedChequeId The ID of the selected cheque
 * @returns A PayWith component (used in TSR, Handy QSR and Handy TSR) with a split payment option
 */
export default function PayWith({ paidAmount, onSelect, selectedChequeId, isHandyQSR }: Props) {
  const { i18n } = useI18n();
  const splitPaymentModal = useModal();
  const logger = useLogger('pay-with');
  const staffCan = useStaffCan();
  const deviceInfo = useDeviceInfo();

  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  const isHandyTSR = deviceInfo?.isHandheldTerminal && !isHandyQSR;

  const splitPaymentButton = useButton({
    onClick: () => {
      splitPaymentModal.show();
    },
    disabled: !staffCan.MAKE_PAYMENT || (isHandyTSR && deviceWakeState.syncingTableEvents),
    disabledToast:
      isHandyTSR && deviceWakeState.syncingTableEvents
        ? i18n('Handy.Tables.LoadingEvents')
        : i18n('Staff.NoPermission.MakePayment'),
  });

  return (
    <Wrapper>
      <PayWithButton
        selectedChequeId={selectedChequeId}
        paidAmount={paidAmount}
        onPay={(methodId, variant) => onSelect(methodId, undefined, variant)}
        splitPaymentButton={
          <BlueButton fluid {...splitPaymentButton}>
            {i18n('TM.SplitPayment')}
          </BlueButton>
        }
      />

      <Modal
        isShowing={splitPaymentModal.isShowing}
        hide={splitPaymentModal.hide}
        backdropClose={false}
        zIndex={50}>
        <SplitPayment
          selectedChequeId={selectedChequeId}
          onClose={splitPaymentModal.hide}
          initiatePayment={(methodId: string, customAmount?: number) => {
            splitPaymentModal.hide();
            onSelect(methodId, customAmount);
          }}
        />
      </Modal>
    </Wrapper>
  );
}
